import { NavItem, TableStaticActions, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useNavigate } from 'react-router'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getInvoiceDocumentRouteBase } from '@features/invoices/utils/getInvoiceDocumentRouteBase'

import { useOrganizationViewUrl } from '../../../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { useContactDetail } from '../../../../contexts/contactDetailContext'
import { ContactPersonsTableActionType } from '../../enums/contactPersonsTableActionType'
import { ContactPersonModal } from '../ContactPersonModal'
import { DeleteContactPersonModal } from '../DeleteContactPersonModal'

interface ContactPersonsTableStaticActionsProps {
  contactPersonId: string
  staticActions: NavItem<ContactPersonsTableActionType>[]
}

export const ContactPersonsTableStaticActions = ({
  contactPersonId,
  staticActions,
}: ContactPersonsTableStaticActionsProps): ReactElement => {
  const navigate = useNavigate()
  const { contact } = useContactDetail()
  const deleteModalId = `${contactPersonId}-delete-contact-person`
  const editModalId = `${contactPersonId}-edit-contact-person`
  const { open: openDeleteModal } = useModal(deleteModalId)
  const { open: openEditModal } = useModal(editModalId)
  const contactPerson = contact.contactPersons?.find((cp) => cp.id === contactPersonId)

  const { url: invoicesNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Invoice)),
  )
  const { url: quotesNewUrl } = useOrganizationViewUrl(
    reactRoute.organization.invoices.getNewDocumentRoute(getInvoiceDocumentRouteBase(InvoiceDocumentType.Quote)),
  )

  const handleStaticActionSelect = useCallback(
    (_id: string, value: ContactPersonsTableActionType) => {
      if (value === ContactPersonsTableActionType.CreateInvoice && invoicesNewUrl) {
        navigate(`${invoicesNewUrl}?contact_id=${contact.id}`)
      } else if (value === ContactPersonsTableActionType.CreateQuote && quotesNewUrl) {
        navigate(`${quotesNewUrl}?contact_id=${contact.id}`)
      } else if (value === ContactPersonsTableActionType.Edit) {
        openEditModal()
      } else if (value === ContactPersonsTableActionType.Delete) {
        openDeleteModal()
      }
    },
    [contact.id, invoicesNewUrl, navigate, openDeleteModal, openEditModal, quotesNewUrl],
  )

  return (
    <>
      <TableStaticActions items={staticActions} onSelect={handleStaticActionSelect} />
      <ContactPersonModal contactId={contact.id} contactPerson={contactPerson} id={editModalId} />
      <DeleteContactPersonModal contactPersonId={contactPersonId} id={deleteModalId} />
    </>
  )
}
